// =============================================================
// FLOW (b): Device Card — the 7-tab full-bleed asset detail, ported from
// scripts/perpetuum-device-card.jsx, now reading REAL data from /api/v1.
//
// Tabs: Overview · Maintenance · Measurements · Downtimes · Files · Spares ·
// History. Open work orders come from the live tasks list (filtered to the
// device); the Spares tab shows the booking math (Available = In stock −
// Booked) from the live stock levels. The measurement ThresholdBar uses the
// core LL/L/H/HH tolerance rule; the maintenance OverdueBar uses the core
// relative-overdue rule. No synthetic prototype data.
// =============================================================

import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import type { Device, Spare, StatusKind, Task } from "@perpetuum/core";
import { ApiError, availableQty, relativeOverdue } from "@perpetuum/core";
import { Icon, ICONS, StatusChip, OverdueBar, Btn, ThresholdBar } from "../components/primitives.js";
import { useScopedT } from "../lib/i18n-keys.js";
import { useAuth } from "../app/AuthContext.js";
import { useChrome } from "../app/ChromeContext.js";
import {
  useAddDeviceFile,
  useAddMeasurementReading,
  useCollection,
  useDeviceFiles,
  useDeviceHistory,
  useDeviceMeasurementPoints,
  useRegisterDowntime,
  type Row,
} from "../lib/queries.js";

type TabKey = "overview" | "maintenance" | "measurements" | "downtimes" | "files" | "spares" | "history";

const DEVICE_STATUS_CHIP: Record<string, [StatusKind, string]> = {
  operational: ["success", "dc.status.operational"],
  maintenance: ["warning", "dc.status.maintenance"],
  down: ["critical", "dc.status.down"],
  decommissioned: ["paused", "dc.status.decommissioned"],
};

/** A spare row joined with its live stock level for the booking-math table. */
export interface DeviceSpareRow {
  spare: Spare;
  inStock: number;
  booked: number;
}

/** A measurement point (LL/L/H/HH) + the current reading, for the bar. */
export interface MeasurementRow {
  name: string;
  unit?: string;
  ll: number;
  l: number;
  h: number;
  hh: number;
  value: number;
}

const TABS: [TabKey, string, string][] = [
  ["overview", "dc.tab.overview", ICONS.gauge],
  ["maintenance", "dc.tab.maintenance", ICONS.wrench],
  ["measurements", "dc.tab.measurements", ICONS.gauge],
  ["downtimes", "dc.tab.downtimes", ICONS.down],
  ["files", "dc.tab.files", ICONS.file],
  ["spares", "dc.tab.spares", ICONS.box],
  ["history", "dc.tab.history", ICONS.history],
];

// ---- Live device-card write tabs (9.2) ------------------------------------

/** Measurements tab — live points + readings; add-a-reading writes the band. */
function MeasurementsTab({ deviceId, enabled }: { deviceId: string; enabled: boolean }) {
  const t = useScopedT();
  const { can } = useAuth();
  const { toast } = useChrome();
  const pointsQ = useDeviceMeasurementPoints(deviceId, enabled);
  const addReading = useAddMeasurementReading();
  const [pointId, setPointId] = useState<string>("");
  const [value, setValue] = useState("");

  const points = pointsQ.data ?? [];
  useEffect(() => {
    if (!pointId && points[0]) setPointId(points[0].id);
  }, [points, pointId]);

  const v = parseFloat(value);
  const onAdd = async () => {
    if (!pointId || isNaN(v)) return;
    try {
      const res = await addReading.mutateAsync({ deviceId, pointId, value: v });
      toast(`${t("dc.measure.recorded")} (${res.band})`, res.band === "alarm" ? "warning" : "success");
      setValue("");
    } catch {
      toast(t("login.err.network_error"), "error");
    }
  };

  return (
    <div>
      <div className="dc-sec">
        {t("dc.measure.points")} <span className="ad-sec-count">{points.length}</span>
        <span className="dc-sec-hint">{t("dc.measure.lastReading")}</span>
      </div>
      {points.length > 0 ? (
        points.map((p) => (
          <ThresholdBar
            key={p.id}
            p={{
              name: p.name,
              unit: p.unit ?? undefined,
              ll: p.ll,
              l: p.l,
              h: p.h,
              hh: p.hh,
              value: p.latestValue ?? (p.l + p.h) / 2,
            }}
          />
        ))
      ) : (
        <div className="ad-none">{t("dc.measure.noTemplate")}</div>
      )}

      {points.length > 0 && (
        <div className="d-card" style={{ marginTop: 14 }}>
          <div className="d-card-body" style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
            <div className="d-field" style={{ flex: 1, minWidth: 180 }}>
              <label htmlFor="mp-point">{t("dc.tab.measurements")}</label>
              <div className="field-ctrl">
                <select id="mp-point" value={pointId} onChange={(e) => setPointId(e.target.value)} data-testid="mp-point">
                  {points.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                      {p.unit ? ` (${p.unit})` : ""}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="d-field" style={{ width: 140 }}>
              <label htmlFor="mp-value">{t("dc.measure.value")}</label>
              <div className="field-ctrl">
                <input id="mp-value" type="number" step="any" value={value} onChange={(e) => setValue(e.target.value)} data-testid="mp-value" />
              </div>
            </div>
            <Btn
              variant="primary"
              size="core"
              data-testid="mp-add"
              disabled={!can("dev", "write") || isNaN(v) || addReading.isPending}
              onClick={() => void onAdd()}
            >
              {t("dc.measure.add")}
            </Btn>
          </div>
        </div>
      )}
    </div>
  );
}

interface DowntimeRow extends Row {
  deviceId?: string;
  categoryId?: string;
  startedAt?: string;
  endedAt?: string | null;
  durationMin?: number | null;
  reason?: string | null;
}

/** Downtimes tab — live device downtimes + a quick "log downtime" form. */
function DowntimesTab({ deviceId }: { deviceId: string }) {
  const t = useScopedT();
  const { can } = useAuth();
  const { toast } = useChrome();
  const allQ = useCollection<DowntimeRow>("downtimes");
  const catsQ = useCollection<Row>("downtime-categories");
  const register = useRegisterDowntime();
  const [categoryId, setCategoryId] = useState("");
  const [reason, setReason] = useState("");

  const cats = catsQ.data ?? [];
  const catById = useMemo(() => new Map(cats.map((c) => [c.id, c])), [cats]);
  const rows = (allQ.data ?? []).filter((d) => d.deviceId === deviceId);

  const onLog = async () => {
    if (!can("downtime", "write")) {
      toast(t("downtimes.noPerm"), "warning");
      return;
    }
    try {
      await register.mutateAsync({ deviceId, categoryId: categoryId || undefined, reason: reason || undefined, startedAt: new Date().toISOString() });
      toast(t("downtimes.logged"), "success");
      setReason("");
    } catch {
      toast(t("login.err.network_error"), "error");
    }
  };

  return (
    <div>
      <div className="dc-sec">
        {t("dc.downtime.log")} <span className="ad-sec-count">{rows.length}</span>
      </div>
      {rows.length > 0 ? (
        rows.map((r) => {
          const cat = r.categoryId ? catById.get(r.categoryId) : undefined;
          return (
            <div className="dc-tle" key={r.id}>
              <span className="dc-tle-dot info">
                <Icon d={ICONS.down} size={13} />
              </span>
              <div>
                <div className="dc-tle-title">{r.reason ?? t("dc.downtime.default")}</div>
                <div className="dc-tle-meta">
                  {cat ? String(cat.name) : "—"} ·{" "}
                  {r.durationMin != null ? t("dc.downtime.min", { n: r.durationMin }) : t("dc.downtime.ongoing")} ·{" "}
                  {r.startedAt ? new Date(r.startedAt).toLocaleString() : ""}
                </div>
              </div>
            </div>
          );
        })
      ) : (
        <div className="ad-none">{t("dc.downtime.none")}</div>
      )}

      <div className="d-card" style={{ marginTop: 14 }}>
        <div className="d-card-body" style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div className="d-field" style={{ minWidth: 170 }}>
            <label htmlFor="dc-dt-cat">{t("downtimes.col.cause")}</label>
            <div className="field-ctrl">
              <select id="dc-dt-cat" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                <option value="">—</option>
                {cats.map((c) => (
                  <option key={c.id} value={c.id}>
                    {String(c.name)}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="d-field" style={{ flex: 1, minWidth: 180 }}>
            <label htmlFor="dc-dt-reason">{t("downtimes.note")}</label>
            <div className="field-ctrl">
              <input id="dc-dt-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t("downtimes.notePlaceholder")} />
            </div>
          </div>
          <Btn variant="stop" size="core" data-testid="dc-log-downtime" disabled={!can("downtime", "write") || register.isPending} onClick={() => void onLog()}>
            {t("dc.downtime.add")}
          </Btn>
        </div>
      </div>
    </div>
  );
}

/** Files tab — live file refs + attach-a-reference (the ref, not the bytes). */
function FilesTab({ deviceId }: { deviceId: string }) {
  const t = useScopedT();
  const { can } = useAuth();
  const { toast } = useChrome();
  const filesQ = useDeviceFiles(deviceId);
  const addFile = useAddDeviceFile();
  const [filename, setFilename] = useState("");
  const [url, setUrl] = useState("");

  const files = filesQ.data ?? [];

  const onAttach = async () => {
    if (!filename || !url) return;
    try {
      await addFile.mutateAsync({ deviceId, url, filename, sizeBytes: 0 });
      toast(t("dc.files.added"), "success");
      setFilename("");
      setUrl("");
    } catch (e) {
      if (e instanceof ApiError) toast(t("login.err.network_error"), "error");
      else toast(t("login.err.network_error"), "error");
    }
  };

  return (
    <div>
      <div className="dc-sec">
        {t("dc.files.documents")} <span className="ad-sec-count">{files.length}</span>
      </div>
      {files.length > 0 ? (
        <table className="d-table" data-testid="dc-files-table">
          <thead>
            <tr>
              <th>{t("dc.files.colFile")}</th>
              <th>{t("dc.files.colType")}</th>
              <th>{t("dc.files.colAdded")}</th>
            </tr>
          </thead>
          <tbody>
            {files.map((f) => (
              <tr key={f.id} className="rowhover">
                <td>
                  <a href={f.url} target="_blank" rel="noreferrer">
                    {f.filename}
                  </a>
                </td>
                <td className="mono">{f.contentType ?? "—"}</td>
                <td>{f.createdAt ? new Date(f.createdAt).toLocaleString() : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="ad-none">{t("dc.files.none")}</div>
      )}

      <div className="d-card" style={{ marginTop: 14 }}>
        <div className="d-card-body" style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div className="d-field" style={{ minWidth: 160 }}>
            <label htmlFor="dc-file-name">{t("dc.files.add")}</label>
            <div className="field-ctrl">
              <input id="dc-file-name" value={filename} onChange={(e) => setFilename(e.target.value)} placeholder={t("dc.files.namePlaceholder")} data-testid="dc-file-name" />
            </div>
          </div>
          <div className="d-field" style={{ flex: 1, minWidth: 200 }}>
            <label htmlFor="dc-file-url">URL</label>
            <div className="field-ctrl">
              <input id="dc-file-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder={t("dc.files.urlPlaceholder")} data-testid="dc-file-url" />
            </div>
          </div>
          <Btn variant="primary" size="core" data-testid="dc-file-add" disabled={!can("dev", "write") || !filename || !url || addFile.isPending} onClick={() => void onAttach()}>
            {t("dc.files.upload")}
          </Btn>
        </div>
      </div>
    </div>
  );
}

/** History tab — the unified live timeline (tasks + downtimes + readings). */
function HistoryTab({ deviceId }: { deviceId: string }) {
  const t = useScopedT();
  const histQ = useDeviceHistory(deviceId);
  const events = histQ.data ?? [];
  const iconFor = (kind: string) =>
    kind === "downtime" ? ICONS.down : kind === "reading" ? ICONS.gauge : kind === "movement" ? ICONS.box : ICONS.wrench;
  return (
    <div>
      <div className="dc-sec">{t("dc.history.timeline")}</div>
      {histQ.isLoading && <div className="ad-none">{t("common.loading")}</div>}
      {!histQ.isLoading && events.length === 0 && <div className="ad-none">{t("dc.history.none")}</div>}
      {events.map((e, i) => (
        <div className="dc-tle" key={i}>
          <span className={`dc-tle-dot ${e.kind === "downtime" ? "crit" : "info"}`}>
            <Icon d={iconFor(e.kind)} size={13} />
          </span>
          <div>
            <div className="dc-tle-title">{e.title}</div>
            <div className="dc-tle-meta">
              {e.kind}
              {e.detail ? ` · ${e.detail}` : ""} · {new Date(e.at).toLocaleString()}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/** A custom-field definition as returned by /custom-field-defs (loose row). */
export interface CustomFieldDefLite {
  key: string;
  label: string;
}

export function DeviceCard({
  device,
  crumb,
  tasks,
  spareRows,
  customFieldDefs = [],
  measurementsEnabled = true,
  onClose,
}: {
  device: Device;
  crumb: string;
  /** Open work orders for this device (from the live tasks list). */
  tasks: Task[];
  /** Spare rows + live stock levels (booking math). */
  spareRows: DeviceSpareRow[];
  /**
   * Custom-field defs (key → label) so the card shows the human label
   * (e.g. "Kritiskums") instead of the JSONB key (`cf_1`). The device's
   * `customFields` bag is keyed by `CustomFieldDef.key`.
   */
  customFieldDefs?: CustomFieldDefLite[];
  /** Measurement points + current readings (LL/L/H/HH) — kept for back-compat. */
  measurements?: MeasurementRow[];
  /** Is the measurements module enabled for this tenant? (gates the live read.) */
  measurementsEnabled?: boolean;
  onClose: () => void;
}) {
  const t = useScopedT();
  const [tab, setTab] = useState<TabKey>("overview");

  // Map a custom-field JSONB key → its human label; fall back to the key when
  // no def is found (e.g. ad-hoc/unmigrated fields).
  const cfLabelByKey = useMemo(
    () => new Map(customFieldDefs.map((d) => [d.key, d.label])),
    [customFieldDefs],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Close the card (clearing its scrim/overlay) on a ROUTE CHANGE. Without this,
  // navigating away (e.g. via the Ctrl+K command palette or a nav link) while
  // the card is open could leave the fixed-position scrim lingering over the new
  // route. We skip the initial mount so opening the card doesn't self-close, and
  // the unmount cleanup below guarantees the overlay is torn down regardless.
  const location = useLocation();
  const mountPathRef = useRef(location.pathname + location.search);
  useEffect(() => {
    const current = location.pathname + location.search;
    if (current !== mountPathRef.current) {
      onClose();
    }
  }, [location.pathname, location.search, onClose]);

  const [chipKind, chipLabelKey] = DEVICE_STATUS_CHIP[device.status ?? "operational"] ?? ["paused", ""];
  const chipLabel = chipLabelKey ? t(chipLabelKey) : "—";
  const openWos = tasks.filter((x) => !x.isCompleted);
  const lowCount = spareRows.filter((s) => availableQty(s.inStock, s.booked) < (s.spare.minQty ?? 0)).length;

  // Maintenance intervals come off the device.interval (single PM) plus dueDays.
  const intervals = useMemo(() => {
    return openWos
      .filter((x) => x.interval || x.dueDays != null)
      .map((x) => {
        const intervalUnits = x.interval?.calendarDays ?? x.interval?.counterHours ?? 0;
        const late = x.dueDays && x.dueDays > 0 ? x.dueDays : 0;
        return { task: x, late, intervalUnits, kind: x.interval?.counterHours ? "counter" : "time" as const };
      });
  }, [openWos]);

  return (
    <div className="dc-scrim" onClick={onClose}>
      <div className="dc-card" onClick={(e) => e.stopPropagation()}>
        <div className="dc-head">
          <div className="dc-head-main">
            <div className="dc-crumb">{crumb}</div>
            <div className="dc-title-row">
              <span className="dc-bigico">
                <Icon d={ICONS.wrench} size={22} />
              </span>
              <div>
                <h2 className="dc-title">{device.name}</h2>
                <div className="dc-code">
                  {device.inventoryNumber ?? device.serialNumber ?? device.id.slice(0, 8)}
                </div>
              </div>
              <StatusChip kind={chipKind}>{chipLabel}</StatusChip>
            </div>
          </div>
          <div className="dc-head-actions">
            <button className="dc-close" onClick={onClose} aria-label={t("dc.close")}>
              <Icon d={ICONS.x} size={20} />
            </button>
          </div>
        </div>

        <div className="dc-stats">
          <div className="dc-stat">
            <span className="k">{t("dc.openWos")}</span>
            <span className="v">{openWos.length}</span>
          </div>
          <div className="dc-stat">
            <span className="k">{t("dc.workHours")}</span>
            <span className="v">{device.workHours ?? "—"}</span>
          </div>
          <div className="dc-stat">
            <span className="k">{t("dc.nfc")}</span>
            <span className="v mono">{device.nfcTagUid ?? "—"}</span>
          </div>
        </div>

        <div className="dc-tabs" role="tablist" aria-label={t("dc.sectionsLabel")}>
          {TABS.map(([k, labelKey, ic]) => (
            <button
              key={k}
              role="tab"
              id={`dc-tab-${k}`}
              aria-selected={tab === k}
              aria-controls="dc-tabpanel"
              tabIndex={tab === k ? 0 : -1}
              className={`dc-tab ${tab === k ? "active" : ""}`}
              onClick={() => setTab(k)}
            >
              <Icon d={ic} size={15} />
              {t(labelKey)}
              {k === "spares" && lowCount > 0 && <span className="dc-tab-n warn">{lowCount}</span>}
            </button>
          ))}
        </div>

        <div className="dc-body" role="tabpanel" id="dc-tabpanel" aria-labelledby={`dc-tab-${tab}`}>
          {tab === "overview" && (
            <div className="dc-grid2">
              <div>
                <div className="dc-sec">{t("dc.identity")}</div>
                <div className="dc-kv">
                  <span className="k">{t("dc.status")}</span>
                  <span className="v">{chipLabel}</span>
                </div>
                <div className="dc-kv">
                  <span className="k">{t("dc.serial")}</span>
                  <span className="v mono">{device.serialNumber ?? device.inventoryNumber ?? "—"}</span>
                </div>
                <div className="dc-kv">
                  <span className="k">{t("dc.workHours")}</span>
                  <span className="v">{device.workHours ?? "—"}</span>
                </div>
                {device.customFields &&
                  Object.entries(device.customFields).map(([k, v]) => (
                    <div className="dc-kv" key={k}>
                      <span className="k" data-testid={`dc-cf-${k}`}>{cfLabelByKey.get(k) ?? k}</span>
                      <span className="v">{String(v ?? "—")}</span>
                    </div>
                  ))}
              </div>
              <div>
                <div className="dc-sec">
                  {t("dc.openWos")} <span className="ad-sec-count">{openWos.length}</span>
                </div>
                {openWos.length > 0 ? (
                  openWos.map((w) => (
                    <div className="ad-wo" key={w.id}>
                      <span className={`wo-led ${w.priority === "critical" ? "crit" : w.priority === "high" ? "warn" : "ok"}`} />
                      <span className="wo-id">{w.number ?? w.id.slice(0, 8)}</span>
                      <span>{w.title}</span>
                    </div>
                  ))
                ) : (
                  <div className="ad-none">{t("dc.noOpenWos")}</div>
                )}
              </div>
            </div>
          )}

          {tab === "maintenance" && (
            <div>
              <div className="dc-sec">
                {t("dc.maint.intervals")} <span className="ad-sec-count">{intervals.length}</span>
              </div>
              {intervals.length > 0 ? (
                intervals.map(({ task, late, intervalUnits, kind }) => {
                  const ov = intervalUnits > 0 ? relativeOverdue(late, intervalUnits) : null;
                  const band = ov ? (ov.band === "alarm" ? "crit" : ov.band) : "ok";
                  return (
                    <div className="dc-iv" key={task.id}>
                      <span className={`dc-iv-led ${band}`} />
                      <div>
                        <div className="dc-iv-name">{task.title}</div>
                        <div className="dc-iv-meta">{task.serviceName ?? "PM"}</div>
                      </div>
                      <div className="dc-iv-kind">
                        <Icon d={kind === "counter" ? ICONS.gauge : ICONS.clock} size={13} />
                        {kind === "counter" ? t("dc.maint.kind.counter") : t("dc.maint.kind.time")}
                      </div>
                      <div className="dc-iv-every">
                        {intervalUnits} {kind === "counter" ? "h" : "d"}
                      </div>
                      {late > 0 && intervalUnits > 0 ? (
                        <div className="dc-iv-overdue">
                          <OverdueBar late={late} interval={intervalUnits} />
                          <span className={`dc-iv-next ${band}`}>
                            {late} {kind === "counter" ? "h" : "d"} / {ov?.percent}%
                          </span>
                        </div>
                      ) : (
                        <div className="dc-iv-next ok">{t("dc.maint.onSchedule")}</div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="ad-none">{t("dc.maint.none")}</div>
              )}
            </div>
          )}

          {tab === "measurements" && <MeasurementsTab deviceId={device.id} enabled={measurementsEnabled} />}

          {tab === "downtimes" && <DowntimesTab deviceId={device.id} />}

          {tab === "files" && <FilesTab deviceId={device.id} />}

          {tab === "spares" && (
            <div>
              <div className="dc-sec">
                {t("dc.spares.mapped")} <span className="ad-sec-count">{spareRows.length}</span>
                <span className="dc-sec-hint">{t("dc.spares.hint")}</span>
              </div>
              {spareRows.length > 0 ? (
                <table className="d-table">
                  <thead>
                    <tr>
                      <th>{t("dc.spares.colCode")}</th>
                      <th>{t("dc.spares.colPart")}</th>
                      <th className="num">{t("dc.spares.colInStock")}</th>
                      <th className="num">{t("dc.spares.colBooked")}</th>
                      <th className="num">{t("dc.spares.colAvailable")}</th>
                      <th className="num">{t("dc.spares.colMin")}</th>
                      <th>{t("dc.spares.colStatus")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {spareRows.map((s) => {
                      const avail = availableQty(s.inStock, s.booked);
                      const low = avail < (s.spare.minQty ?? 0);
                      return (
                        <tr key={s.spare.id} className="rowhover">
                          <td className="mono">{s.spare.code ?? "—"}</td>
                          <td>{s.spare.name}</td>
                          <td className="num">{s.inStock}</td>
                          <td className="num">
                            {s.booked > 0 ? (
                              <span className="dc-booked">{s.booked}</span>
                            ) : (
                              <span style={{ color: "var(--sys-fg-mute)" }}>0</span>
                            )}
                          </td>
                          <td className="num">
                            <b className={low ? "dc-avail-low" : "dc-avail-ok"}>{avail}</b>
                          </td>
                          <td className="num">{s.spare.minQty ?? 0}</td>
                          <td>
                            {low ? (
                              <StatusChip kind="warning">{t("dc.spares.belowMin")}</StatusChip>
                            ) : (
                              <StatusChip kind="success">{t("dc.spares.ok")}</StatusChip>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div className="ad-none">{t("dc.spares.none")}</div>
              )}
            </div>
          )}

          {tab === "history" && <HistoryTab deviceId={device.id} />}
        </div>
      </div>
    </div>
  );
}
